import { call, put, takeLatest, select, delay } from 'redux-saga/effects';
import { Auth } from 'aws-amplify';
import isEmpty from 'lodash/isEmpty';
import i18n from 'i18next';
import moment from 'moment-timezone';

import { ProfileLanguage } from 'types';

import {
  doCheckIsAuthorized,
  doLogin,
  doLogout,
} from 'store/actionCreators/auth';
// import { doLoginChat } from 'store/actionCreators/chat';
import { getCurrentUserSubscription } from 'store/actionCreators/subscriptions';
import { getCurrentIdToken, getFullAuthUser } from 'store/selectors/auth';
import { getCurrentUser } from 'services/auth';
import { setHeaders, clearHeaders } from 'services';
import { toast } from 'utils/toastAbstraction';
import {
  doGetNotifications,
  doGetTotalChatCount,
  doGetUnRespondedEventsCount,
  editProfile,
  doChangeProfileSettings,
} from '../actionCreators/profile';
import { doClearCommunities } from '../actionCreators/communities';

// Helper function to extract Google Analytics parameters
const getGoogleAnalyticsParams = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const gaParams = new URLSearchParams();

  for (const [key, value] of searchParams.entries()) {
    if (key.startsWith('_g')) {
      gaParams.append(key, value);
    }
  }

  return gaParams.toString();
};

// Helper function to detect browser language and map to supported language
const detectBrowserLanguage = (): string => {
  const availableLanguages = (i18n.options['languages'] as string[]) || [
    'en',
    'de',
    'fr',
    'it',
    'es',
    'ru',
  ];
  const fallBackLanguage = i18n.options.fallbackLng?.[0] || 'en';

  // Helper to map detected languages to supported ones
  const mapToSupportedLanguage = (detectedList: string[]): string => {
    if (!Array.isArray(detectedList)) return fallBackLanguage;
    for (let lang of detectedList) {
      lang = lang.toLowerCase();
      if (lang.includes('de') || lang.includes('-ch')) return 'de';
      if (lang.startsWith('fr')) return 'fr';
      if (lang.startsWith('it')) return 'it';
      if (lang.startsWith('es')) return 'es';
      if (lang.startsWith('ru')) return 'ru';
      if (lang.startsWith('en')) return 'en';
      // Direct match to available languages
      if (availableLanguages.includes(lang)) return lang;
      // Try base language (e.g., 'de-CH' -> 'de')
      const baseLang = lang ? lang.split('-')[0] : '';
      if (availableLanguages.includes(baseLang)) return baseLang;
    }
    return fallBackLanguage;
  };

  // Get browser languages - convert readonly array to mutable
  const browserLangs = Array.from(navigator.languages || [navigator.language]);
  return mapToSupportedLanguage(browserLangs);
};

function* checkIsAuthorized(payload: ReturnType<typeof doCheckIsAuthorized>) {
  try {
    let cognitoSession = yield Auth.currentSession();
    const currentUserToken = yield select(getCurrentIdToken);

    if (
      new Date(cognitoSession.idToken?.payload.exp * 1000).getTime() <=
      new Date().getTime()
    ) {
      const cognitoUser = yield Auth.currentAuthenticatedUser();

      yield new Promise((resolve, reject) => {
        cognitoUser.refreshSession(
          cognitoSession.refreshToken,
          (err, session) => {
            if (err) reject();

            const { idToken } = session;
            cognitoSession = session;

            setHeaders(idToken.jwtToken);
            resolve(null);
          },
        );
      });
    } else if (
      !currentUserToken ||
      cognitoSession.idToken.jwtToken !== currentUserToken ||
      !cognitoSession.idToken?.payload?.id
    ) {
      yield setHeaders(cognitoSession.idToken.jwtToken);
    }

    if (
      !currentUserToken ||
      cognitoSession.idToken.jwtToken !== currentUserToken ||
      !cognitoSession.idToken?.payload?.id
    ) {
      const user = yield call(
        getCurrentUser,
        cognitoSession.idToken.payload.id || cognitoSession.idToken.payload.sub,
      );

      yield put(
        doCheckIsAuthorized.success({
          ...user,
          idToken: cognitoSession.idToken.jwtToken,
        }),
      );
      if (payload?.payload?.onSuccess) {
        yield payload.payload.onSuccess(user, cognitoSession);
      }
      yield put(doGetNotifications({ userId: user.id }));
      yield put(doGetTotalChatCount({ userId: user.id }));
      yield put(doGetUnRespondedEventsCount({ userId: user.id }));

      const timezone = moment.tz.guess();

      if (
        user.profile &&
        (!user.profile.timeZone || user.profile.timeZone !== timezone) &&
        timezone
      ) {
        yield put(
          editProfile({
            data: {
              timeZone: timezone,
            },
            id: user.id,
          }),
        );
      }

      // Determine which language to use
      let languageToUse = user.settings.language;

      // If user has no language set or it's the default 'en', detect browser language
      if (!languageToUse || languageToUse === 'en') {
        const detectedLanguage = detectBrowserLanguage();
        // Only use detected language if it's different from 'en' (to avoid unnecessary updates)
        if (detectedLanguage !== 'en') {
          languageToUse = detectedLanguage;
          // Update user's language preference in backend
          yield put(
            doChangeProfileSettings.trigger({
              userId: user.id,
              data: { language: detectedLanguage as ProfileLanguage },
            }),
          );
        }
      }

      if (i18n.language !== languageToUse) {
        yield delay(0);
        yield i18n.changeLanguage(languageToUse);
      }
    } else {
      const user = yield select(getFullAuthUser);
      yield put(doCheckIsAuthorized.success(user));
      if (payload?.payload?.onSuccess) {
        yield payload.payload.onSuccess(user, cognitoSession);
      }
      // Determine which language to use
      let languageToUse = user.settings.language;

      // If user has no language set or it's the default 'en', detect browser language
      if (!languageToUse || languageToUse === 'en') {
        const detectedLanguage = detectBrowserLanguage();
        // Only use detected language if it's different from 'en' (to avoid unnecessary updates)
        if (detectedLanguage !== 'en') {
          languageToUse = detectedLanguage;
          // Update user's language preference in backend
          yield put(
            doChangeProfileSettings.trigger({
              userId: user.id,
              data: { language: detectedLanguage as ProfileLanguage },
            }),
          );
        }
      }

      if (i18n.language !== languageToUse) {
        yield delay(0);
        yield i18n.changeLanguage(languageToUse);
      }
    }
    yield put(getCurrentUserSubscription.trigger());
  } catch (e) {
    yield put(doCheckIsAuthorized.failure());
    if (e.code && e.message) {
      yield toast(
        i18n.t([`errors.${e.code}`, 'errors.errorToast'], {
          message: e.message,
        }),
        {
          appearance: 'error',
          autoDismiss: true,
        },
      );
    }
  }
}

function* login({ payload }: ReturnType<typeof doLogin>) {
  try {
    yield put(doLogin.request());
    const cognitoUser = yield Auth.currentUserInfo();
    if (isEmpty(cognitoUser)) {
      yield Auth.signIn(payload.email, payload.password);
      yield put(doLogin.success());

      // Get Google Analytics parameters
      const gaParams = getGoogleAnalyticsParams();

      // Trigger authorization check with navigation callback
      yield put(
        doCheckIsAuthorized.trigger({
          onSuccess: () => {
            // Use history.pushState to update URL without reload
            const newUrl = `/profile${gaParams ? `?${gaParams}` : ''}`;
            window.history.pushState({}, '', newUrl);
            // Trigger a custom event to notify React Router
            window.dispatchEvent(new PopStateEvent('popstate'));
          },
        }),
      );
    }
  } catch (error) {
    yield put(doLogin.failure());

    if (payload.onFailure) {
      yield call(payload.onFailure, error);
    }
  }
}

function* logout() {
  try {
    yield Auth.signOut();
    yield clearHeaders();
    yield put(doClearCommunities.trigger());
    yield put(doCheckIsAuthorized.failure());
  } catch (e) {
    yield put(doLogout.failure(e));
  }
}

export default function* () {
  yield takeLatest(doLogout.TRIGGER, logout);
  yield takeLatest(doCheckIsAuthorized.TRIGGER, checkIsAuthorized);
  yield takeLatest(doLogin.TRIGGER, login);
}
